import React from 'react'
import { useTheme } from 'styled-components'
import { Icon } from '../../atoms/Icon'
import { Button } from '../../atoms/Button'
import { IconButton } from '../../atoms/IconButton'
import { Text } from '../../atoms/Text'
import {
  ResizableDetailPanel,
  DetailPanelHeader,
  DetailPanelContent,
  DetailPanelFooter,
} from '../ResizableDetailPanel'
import { useListingDetail } from './ListingDetail.hooks'
import {
  ListingDetailProps,
  ListingDetailTranslations,
} from './ListingDetail.types'
import { ListingDetailContent } from './ListingDetailContent'

const defaultTranslations: ListingDetailTranslations = {
  loading: 'Loading listing details...',
  error: 'Error',
  published: 'Published',
  status: 'Status',
  onlineFor: 'Online for',
  onlineDuring: 'Online during',
  active: 'Active',
  expired: 'Expired',
  rooms: 'Rooms',
  livingArea: 'Living Area',
  landArea: 'Land Area',
  bathrooms: 'Bathrooms',
  built: 'Built',
  description: 'Description',
  propertyDetails: 'Property Details',
  featuresAmenities: 'Features & Amenities',
  propertyAgents: 'Property Agents',
  listedOnPortals: 'Listed on Portals',
  priceHistory: 'Price History',
  propertyType: 'Property Type',
  constructionYear: 'Construction Year',
  newConstruction: 'New Construction',
  yes: 'Yes',
  no: 'No',
  floor: 'Floor',
  available: 'Available',
  findAddress: 'Find Address',
  findExactAddress: 'Find the exact address',
  unlinkAddress: 'Unlink address',
  viewOnExplore: 'View on Explore',
  viewInLandAnalysis: 'View in Land Analysis',
  estimate: 'Estimate',
  addToFavorites: 'Add to favorites',
  property: 'Property',
  house: 'House',
  addressNotSpecified: 'Address not specified',
  immediately: 'Immediately',
  unknownAgent: 'Unknown Agent',
  privateVendor: 'Private',
  professionalVendor: 'Professional',
  day: 'day',
  days: 'days',
  approximateLocation: 'Approximate location',
  priceIncrease: 'increase',
  priceDecrease: 'decrease',
  fromPrevious: 'from previous',
}

import { DetailHeaderInfo, HeaderActions } from './ListingDetail.styles'

export const ListingDetail: React.FC<ListingDetailProps> = ({
  isOpen,
  onClose,
  listingId,
  loadListing,
  translations: propTranslations,
  contained = false,
  disableResize = false,
  isSaved = false,
  onToggleSave,
  onFindAddress,
  language = 'en',
  linkedLand,
  onUnlink,
  featureTranslations,
  onViewOnExplore,
  onViewInLandAnalysis,
  onEstimate,
  onViewAgentListings,
  onAddToDeal,
  printMode = false,
  hideFooterActions = false,
  hideContactCard = false,
  hidePortals = false,
  hideMap = false,
}) => {
  const translations = { ...defaultTranslations, ...propTranslations }
  const theme = useTheme()
  const {
    listing,
    loading,
    error,
    images,
    hasImages,
    currentImageIndex,
    goToImage,
    getImageUrl,
    formatPrice,
    formatArea,
    formatRooms,
    formatNumber,
    formatDate,
    formatDuration,
  } = useListingDetail(listingId ? parseInt(listingId) : undefined, loadListing)

  // In print mode, render just the content without the panel wrapper
  if (printMode) {
    return (
      <ListingDetailContent
        listing={listing}
        loading={loading}
        error={error}
        images={images}
        hasImages={hasImages}
        currentImageIndex={currentImageIndex}
        goToImage={goToImage}
        getImageUrl={getImageUrl}
        formatPrice={formatPrice}
        formatArea={formatArea}
        formatRooms={formatRooms}
        formatNumber={formatNumber}
        formatDate={formatDate}
        formatDuration={formatDuration}
        translations={translations}
        language={language}
        linkedLand={linkedLand}
        onUnlink={onUnlink}
        featureTranslations={featureTranslations}
        onFindAddress={onFindAddress}
        onViewAgentListings={onViewAgentListings}
        printMode
        hideContactCard={hideContactCard}
        hidePortals={hidePortals}
        hideMap={hideMap}
      />
    )
  }

  return (
    <ResizableDetailPanel
      isOpen={isOpen}
      onClose={onClose}
      storageKey="listing_detail_panel_width"
      defaultWidth="900px"
      minWidth={400}
      maxWidthPercentage={0.8}
      contained={contained}
      disableResize={disableResize}
    >
      {/* Header with actions */}
      <DetailPanelHeader onClose={onClose}>
        <DetailHeaderInfo>
          <Text variant="caption" color="secondary">
            {listing?.property_category ||
              listing?.property_type ||
              translations.property}
          </Text>
        </DetailHeaderInfo>
        <HeaderActions>
          {onToggleSave && (
            <IconButton
              variant={isSaved ? 'solid' : 'default'}
              aria-label={translations.addToFavorites}
              onClick={onToggleSave}
            >
              <Icon
                name={isSaved ? 'bookmark' : 'bookmark'}
                size="medium"
                color={isSaved ? theme.colors.primary : undefined}
                fill={isSaved ? theme.colors.primary : 'transparent'}
              />
            </IconButton>
          )}
        </HeaderActions>
      </DetailPanelHeader>

      <DetailPanelContent>
        <ListingDetailContent
          listing={listing}
          loading={loading}
          error={error}
          images={images}
          hasImages={hasImages}
          currentImageIndex={currentImageIndex}
          goToImage={goToImage}
          getImageUrl={getImageUrl}
          formatPrice={formatPrice}
          formatArea={formatArea}
          formatRooms={formatRooms}
          formatNumber={formatNumber}
          formatDate={formatDate}
          formatDuration={formatDuration}
          translations={translations}
          language={language}
          linkedLand={linkedLand}
          onUnlink={onUnlink}
          featureTranslations={featureTranslations}
          onFindAddress={onFindAddress}
          onViewAgentListings={onViewAgentListings}
          hideContactCard={hideContactCard}
          hidePortals={hidePortals}
          hideMap={hideMap}
        />
      </DetailPanelContent>

      {!hideFooterActions && (
        <DetailPanelFooter>
          <Button
            variant="secondary"
            disabled={!!listing?.geo_center_popety && !linkedLand}
            onClick={onFindAddress}
          >
            <Icon name="location" size="small" />
            {translations.findAddress}
          </Button>
          <Button
            variant="secondary"
            disabled={
              (!listing?.geo_center_popety && !linkedLand) || !onViewOnExplore
            }
            onClick={() => listing && onViewOnExplore?.(listing)}
          >
            <Icon name="map" size="small" />
            {translations.viewOnExplore}
          </Button>
          <Button
            variant="secondary"
            disabled={
              (!listing?.geo_center_popety && !linkedLand) ||
              !onViewInLandAnalysis
            }
            onClick={() => listing && onViewInLandAnalysis?.(listing)}
          >
            <Icon name="chart-line" size="small" />
            {translations.viewInLandAnalysis}
          </Button>
          <Button
            variant="secondary"
            disabled={
              (!listing?.geo_center_popety && !linkedLand) || !onEstimate
            }
            onClick={() => listing && onEstimate?.(listing)}
          >
            <Icon name="calculator" size="small" />
            {translations.estimate}
          </Button>
          {onAddToDeal && (
            <Button
              variant="secondary"
              disabled={false}
              onClick={() => listing && onAddToDeal?.(listing)}
            >
              <Icon name="git-branch" size="small" />
              {translations.addToDeal || 'Add to Deal'}
            </Button>
          )}
        </DetailPanelFooter>
      )}
    </ResizableDetailPanel>
  )
}
